/**
 * Página "Mi cuenta" — Sprint 11 v2 S11-V2-07.
 *
 * El título visible al vecino es "Mi cuenta" según convención #13. El
 * endpoint sigue siendo `getEstadoCuenta` y la URL del portal
 * `/vecino/estado-cuenta`.
 *
 * Sprint 14 S14-Pagos: Pill + período legible + fecha amigable +
 * SectionHead + EmptyState + InfoNote.
 * Sprint 14 S14-Otros: integraba sección "Otros pagos" en estado actual.
 * Sprint 14 S14-Historial v2: la sección "Otros pagos" + el historial
 * de cuotas pagadas se consolidan en el tab "Historial" (subcomponente
 * `HistorialBody` reusable). El tab "Estado actual" queda solo con las
 * cuotas pendientes — "qué debes pagar hoy".
 */
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import { casasDelVecino } from '../../lib/casas-vecino';
import { getEstadoCuenta } from '../../lib/api/lotes';
import { SaldoAFavorBanner } from '../../components/ui/SaldoAFavorBanner';
import type { CuotaEstado, EstadoCuentaResponse } from '../../lib/api/types';
import { useAuth } from '../../lib/auth/AuthContext';
import { formatCLP, formatFecha } from '../../lib/format';
import { hoy } from '../../lib/tiempo';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DatosCuentaModal } from '../../components/ui/DatosCuentaModal';
import { Pill } from '../../components/ui/Pill';
import { SectionHead } from '../../components/ui/Section';
import { EmptyState } from '../../components/ui/EmptyState';
import { InfoNote } from '../../components/ui/InfoNote';
import { HistorialBody } from './Historial';

type PillVariant = 'pending' | 'success' | 'warning' | 'danger' | 'info';

const ETIQUETA_ESTADO: Record<string, string> = {
  pendiente: 'Pendiente',
  en_abono: 'En abono',
  pagada: 'Pagada',
  vencida: 'Vencida',
  condonada: 'Condonada',
};

const PILL_ESTADO: Record<string, PillVariant> = {
  pendiente: 'pending',
  en_abono: 'info',
  pagada: 'success',
  vencida: 'danger',
  condonada: 'warning',
};

const MESES_CORTO = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
];

/** "2026-S1" → "1er semestre 2026"; "2026-S2" → "2do semestre 2026". */
function periodoLegible(periodo: string): string {
  const m = /^(\d{4})-S(1|2)$/.exec(periodo);
  if (!m) return periodo;
  const [, anio, sem] = m;
  return sem === '1' ? `1er semestre ${anio}` : `2do semestre ${anio}`;
}

/** "2026-07-31" → "31 jul 2026" (consistente con el Dashboard). */
function fechaAmigable(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return formatFecha(iso);
  const [, anio, mm, dd] = m;
  const idx = parseInt(mm, 10) - 1;
  return `${parseInt(dd, 10)} ${MESES_CORTO[idx] ?? mm} ${anio}`;
}

interface FilaCuotaProps {
  cuota: CuotaEstado;
}

function FilaCuota({ cuota }: FilaCuotaProps) {
  const etiqueta = ETIQUETA_ESTADO[cuota.estado] ?? cuota.estado;
  const variant = PILL_ESTADO[cuota.estado] ?? 'pending';
  const vencidaImplicita =
    cuota.saldo_pendiente > 0 &&
    cuota.estado !== 'condonada' &&
    cuota.estado !== 'vencida' &&
    // Comparación de fechas ISO en horario de Santiago (convención #16).
    // `new Date('2027-01-31')` es medianoche UTC = 30-01 21:00 en Chile: la
    // cuota salía "(vencida)" el último día del plazo, con el vecino en regla.
    cuota.vence_el < hoy();

  return (
    <Card>
      <header className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
        <div>
          <h3 className="text-lg font-medium text-text-primary">
            {periodoLegible(cuota.periodo)}
          </h3>
          <p className="text-xs text-text-muted mt-0.5">
            Vence el {fechaAmigable(cuota.vence_el)}
            {vencidaImplicita && (
              <span className="ml-2 text-state-danger font-medium">
                (vencida)
              </span>
            )}
          </p>
        </div>
        <Pill variant={variant}>{etiqueta}</Pill>
      </header>

      <div className="grid grid-cols-3 gap-2 sm:gap-3 text-xs sm:text-sm mb-4">
        <div>
          <p className="text-xs text-text-muted">Monto total</p>
          <p className="font-medium text-text-primary">
            {formatCLP(cuota.monto_total)}
          </p>
        </div>
        <div>
          <p className="text-xs text-text-muted">Aplicado</p>
          <p className="font-medium text-state-success">
            {formatCLP(cuota.monto_aplicado)}
          </p>
        </div>
        <div>
          <p className="text-xs text-text-muted">Saldo pendiente</p>
          <p
            className={`font-medium ${
              cuota.saldo_pendiente > 0
                ? 'text-state-danger'
                : 'text-text-primary'
            }`}
          >
            {formatCLP(cuota.saldo_pendiente)}
          </p>
        </div>
      </div>

      {cuota.pagos_aplicados.length > 0 && (
        <div className="border-t-[0.5px] border-white/[0.08] pt-3">
          <p className="text-xs font-medium text-text-secondary mb-2">
            Pagos aplicados ({cuota.pagos_aplicados.length})
          </p>
          <ul className="space-y-1.5">
            {cuota.pagos_aplicados.map((pago) => (
              <li
                key={pago.pago_id}
                className="flex justify-between items-center text-xs"
              >
                <div>
                  <span className="text-text-primary">
                    {fechaAmigable(pago.fecha_transferencia)}
                  </span>
                  <span className="text-text-muted ml-2">
                    op. {pago.num_operacion}
                  </span>
                </div>
                <span className="font-medium text-state-success">
                  +{formatCLP(pago.monto_aplicado)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

interface EstadoCuentaProps {
  /** Solo para previews dev: inyecta el estado y omite el fetch. */
  estadoOverride?: EstadoCuentaResponse;
}

export function EstadoCuenta({ estadoOverride }: EstadoCuentaProps = {}) {
  const { me } = useAuth();
  // Casas del vecino: tenencias (titular) + casas donde es autorizado.
  const casas = casasDelVecino(me);

  const [loteSeleccionado, setLoteSeleccionado] = useState<string>(
    casas[0]?.lote_id ?? '',
  );
  const [estadoCuenta, setEstadoCuenta] = useState<EstadoCuentaResponse | null>(
    estadoOverride ?? null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // S14-Historial v2: tabs internos. "Estado actual" = qué debes hoy;
  // "Historial" = todo lo que has pagado (cuotas + otros pagos).
  type Tab = 'actual' | 'historial';
  const [tab, setTab] = useState<Tab>('actual');
  // H16: datos para transferir, siempre accesibles desde Mi Cuenta.
  const [datosCuentaAbierto, setDatosCuentaAbierto] = useState(false);

  useEffect(() => {
    // Preview dev: el estado viene inyectado, no se hace fetch.
    if (estadoOverride) return;
    if (!loteSeleccionado) {
      setEstadoCuenta(null);
      return;
    }
    let cancelado = false;
    setLoading(true);
    setError(null);
    getEstadoCuenta(loteSeleccionado)
      .then((data) => {
        if (!cancelado) setEstadoCuenta(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `No pudimos cargar el estado de cuenta (error ${err.status}).`
            : 'No pudimos cargar el estado de cuenta. Intenta recargar.',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [loteSeleccionado, estadoOverride]);

  // Solo cuotas con saldo > 0 (lo que el vecino debe pagar hoy).
  const cuotasPendientes = useMemo(() => {
    if (!estadoCuenta) return [];
    return estadoCuenta.cuotas
      .filter((c) => {
        const archivada =
          (c.estado === 'pagada' || c.estado === 'condonada') &&
          c.saldo_pendiente === 0;
        return !archivada;
      })
      .sort((a, b) => a.vence_el.localeCompare(b.vence_el));
  }, [estadoCuenta]);

  // En preview dev el estado viene inyectado y no hay tenencias del auth;
  // saltamos el guard "sin casa" para poder renderizar la pantalla.
  if (casas.length === 0 && !estadoOverride) {
    return (
      <div className="px-8 py-6">
        <div className="bg-bg-sunken border-b-[0.5px] border-white/[0.08] -mx-8 -mt-6 px-8 pt-6 pb-5 mb-6">
          <div className="text-sm text-text-muted uppercase tracking-wide mb-1">
            Mi cuenta
          </div>
          <h1 className="text-2xl font-medium text-text-primary leading-tight">
            Sin casa asignada
          </h1>
        </div>
        <Card>
          <p className="text-text-secondary">
            No tienes una casa asignada en el portal. Contacta a la comisión.
          </p>
        </Card>
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'actual', label: 'Estado actual' },
    { id: 'historial', label: 'Historial' },
  ];

  return (
    <div>
      <div className="bg-bg-sunken border-b-[0.5px] border-white/[0.08] px-8 pt-6 pb-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-sm text-text-muted uppercase tracking-wide mb-1">
              Mi cuenta
            </div>
            <h1 className="text-2xl font-medium text-text-primary leading-tight">
              {estadoCuenta
                ? `Casa ${estadoCuenta.identificador}`
                : 'Cargando…'}
            </h1>
            {estadoCuenta && tab === 'actual' && (
              <p className="text-base text-text-secondary mt-1">
                {cuotasPendientes.length}{' '}
                {cuotasPendientes.length === 1
                  ? 'cuota pendiente'
                  : 'cuotas pendientes'}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {casas.length > 1 && (
              <label className="text-sm text-text-secondary">
                Casa:{' '}
                <select
                  value={loteSeleccionado}
                  onChange={(e) => setLoteSeleccionado(e.target.value)}
                  className="rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary text-sm ml-2 px-2 py-1 focus:border-orange focus:ring-1 focus:ring-orange/30"
                >
                  {casas.map((t) => (
                    <option key={t.lote_id} value={t.lote_id}>
                      {t.lote_identificador}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <Button
              variant="secondary"
              onClick={() => setDatosCuentaAbierto(true)}
            >
              Cómo pagar
            </Button>
          </div>
        </div>

        {/* Tabs internos (mismo patrón que Mis Datos) */}
        <div className="flex gap-1 mt-5 -mb-[1px]" role="tablist">
          {tabs.map((t) => {
            const activo = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={activo}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activo
                    ? 'text-orange border-orange'
                    : 'text-text-secondary border-transparent hover:text-text-primary'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-8 py-6 space-y-3 max-w-4xl">
        {/* === Tab: Estado actual === */}
        {tab === 'actual' && (
          <>
            {loading && (
              <div className="space-y-3" aria-busy="true">
                {[0, 1, 2].map((i) => (
                  <Card key={i}>
                    <div className="h-4 w-32 bg-bg-elevated rounded mb-3" />
                    <div className="h-3 w-48 bg-bg-elevated/60 rounded" />
                  </Card>
                ))}
              </div>
            )}

            {error && !loading && (
              <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
                {error}
              </div>
            )}

            {!loading && !error && estadoCuenta && (
              <SaldoAFavorBanner monto={estadoCuenta.saldo_a_favor_actual} />
            )}

            {!loading &&
              !error &&
              estadoCuenta &&
              cuotasPendientes.length === 0 && (
                <EmptyState
                  title="Estás al día"
                  description="No tienes cuotas pendientes. Las próximas cuotas semestrales aparecerán acá cuando se generen."
                />
              )}

            {!loading && !error && cuotasPendientes.length > 0 && (
              <section className="space-y-3">
                <SectionHead title="Cuotas pendientes" />
                {cuotasPendientes.map((cuota) => (
                  <FilaCuota key={cuota.cuota_id} cuota={cuota} />
                ))}
              </section>
            )}

            {!loading && !error && estadoCuenta && (
              <InfoNote>
                Las cuotas se cobran por semestre (1er semestre: enero–junio;
                2do semestre: julio–diciembre). Si pagaste y no aparece
                reflejado en tu saldo, contacta a la Comisión con el número
                de operación.
              </InfoNote>
            )}
          </>
        )}

        {/* === Tab: Historial === */}
        {tab === 'historial' && loteSeleccionado && (
          <HistorialBody loteId={loteSeleccionado} />
        )}
      </div>

      {datosCuentaAbierto && (
        <DatosCuentaModal onClose={() => setDatosCuentaAbierto(false)} />
      )}
    </div>
  );
}
